import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';
import { asset } from '../utils/asset';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { config } = useConfig();

  const schoolHomeUrl = '/slmcspage/';

  const navigation = [
    { name: 'Home', href: schoolHomeUrl, external: true },
    { name: 'Services', href: '/services' },
    { name: 'Admission', href: '/admission' },
    { name: 'Forms', href: '/forms' },
    { name: 'Staff', href: '/staff' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path) => !path.startsWith('http') && !path.startsWith('/slmcspage') && location.pathname === path;

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-blue-800 text-white py-2">
        <div className="container-custom">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                {config?.site?.phone || '(555) 123-4567'}
              </div>
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                {config?.site?.email || 'slmcs.gcs@gmail.com'}
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/register" className="hover:text-yellow-300 transition-colors">
                Register
              </Link>
              <Link to="/login" className="hover:text-yellow-300 transition-colors">
                Login
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container-custom">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <a href={schoolHomeUrl} className="flex items-center">
            <img 
              src={asset("/images/logo_school_new.png")} 
              alt={config?.site?.name || 'Guidance Center'}
              className="h-12 w-auto mr-3"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                {config?.site?.name || 'Guidance Center'}
              </h1>
              <p className="text-sm text-gray-600">
                {config?.site?.schoolName || 'Your School Name'}
              </p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              item.external ? (
                <a
                  key={item.name}
                  href={item.href}
                  className="font-medium transition-colors text-gray-700 hover:text-blue-800"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-blue-800 border-b-2 border-blue-800'
                      : 'text-gray-700 hover:text-blue-800'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-800"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <nav className="flex flex-col space-y-2">
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-2 rounded-md font-medium transition-colors text-gray-700 hover:bg-gray-100"
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-blue-800 text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
              <Link
                to="/login"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-2 rounded-md font-medium text-gray-700 hover:bg-gray-100 border-t pt-4 mt-4"
              >
                Login
              </Link>
              <Link
                to="/register"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-2 rounded-md font-medium text-blue-800 hover:bg-gray-100"
              >
                Register
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;